import React, { useState, useMemo, useContext } from 'react';

import { bookingInquiryApis, dashboardBookingInquiryApis } from '@apis/next/bookingInquiryApis';
import { dashboardCustomsRequestApis } from '@apis/next/customsRequestApis';
import { dashboardDeliveryRequestApis } from '@apis/next/deliveryRequestApis';
import CautionIcon from '@assets/img/icons/caution-circle.svg';
import EmptyIcon from '@assets/img/icons/empty-circle.svg';
import InProgressIcon from '@assets/img/icons/in-progress-circle-primary.svg';
import TickIcon from '@assets/img/icons/tick-circle-grey.svg';
import { BookingStatusItem } from '@components/admin/booking/BookingStatusItem';
import { DOUpdateDialog } from '@components/admin/booking/DOUpdateDialog';
import {
  BookingDetailsContext,
  BookingDetailsContextValues,
} from '@components/admin/booking/helpers';
import { MoveOutUpdateDialog } from '@components/admin/booking/MoveOutUpdateDialog';
import { PaymentSlipFormDialog } from '@components/admin/booking/PaymentSlipFormDialog';
import { Button } from '@components/Button';
import { QueryContainer } from '@components/QueryContainer';
import { getInstructionStatusLabel } from '@data/instruction';
import { finishStatuses } from '@data/statusFlow';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import type { CustomsRequestStatusName } from '@models/next/CustomsRequestStatus';
import { DeliveryRequestStatusName } from '@models/next/DeliveryRequestStatus';
import { InstructionStatusName } from '@models/next/Instruction';
import { StatusData } from '@models/next/StatusData';
import { StatusFlows, StatusFlowsHelper } from '@models/next/StatusFlows';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime, includes, stringifySearch } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';

const strings = createStrings({
  ja: {
    customsStatus: '通関状況',
    deliveryStatus: '配送状況',
    doStatus: 'D/O手配',
    moveOutStatus: '出庫手配',
    invoiceStatus: '請求書',
    paymentSlipStatus: '支払伝票',
    seeMore: 'もっと見る',
    close: '閉じる',
    deliveryRequestCompleted: '配車完了',
    completed: '完了',
    create: '作成',
    overdue: '期間切れ',
    ariaLabelIsLast: '現在の状況',
    statusHints: {
      搬出可能: '必ずデバン日を確認してください',
      搬出手配済: '必ずデバン日を確認してください',
    },
    completeSubmitCustomsRequest: '通関依頼済',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  titleContainer: {
    ...common.textGray700,
    ...common.normalText,
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.grey[100],
    height: '50px',
    paddingLeft: '10px',
  },
  statusItem: {
    ...common.textGray700,
    padding: '0 0 15px 15px',
    position: 'relative',
    marginLeft: '8px',
    borderWidth: '0px 0px 0px 1px',
    borderStyle: 'dashed',
    borderColor: theme.palette.grey[200],
    '&:nth-child(1)::before': {
      content: '""',
      position: 'absolute',
      width: '3px',
      height: '10px',
      left: '-3px',
      background: theme.palette.background.paper,
    },
    '&:nth-last-child(2)::before': {
      content: '""',
      position: 'absolute',
      width: '3px',
      height: '100px',
      left: '-3px',
      top: '10px',
      background: theme.palette.background.paper,
    },
    '&:nth-child(1):nth-last-child(2)': {
      borderColor: theme.palette.background.paper,
    },
  },
  statusName: {
    ...common.textGray700,
    ...common.middleText,
    lineHeight: '22px',
  },
  statusDate: {
    ...common.textGray500,
    ...common.smallerText,
    lineHeight: '22px',
  },
  button: {
    ...common.textPrimary,
    ...common.smallerText,
    ...common.underline,
    ...common.baseButton,
    padding: '0 0 15px 15px',
    marginLeft: '8px',
  },
  dot: {
    position: 'absolute',
    borderRadius: '50%',
    width: '6px',
    height: '6px',
    top: '8px',
    left: '-3px',
    background: theme.palette.grey[500],
    '&.primary': {
      ...common.backgroundPrimary,
    },
    '&.error': {
      ...common.backgroundError,
    },
    '&.next': {
      background: 'transparent',
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: Color(theme.palette.primary.main).alpha(0.4).toString(),
    },
  },
  itemsContainer: {
    paddingLeft: '10px',
    paddingTop: '5px',
  },
}));

interface statusFlowsContextValues {
  invalidateStatusFlows: () => Promise<void>;
  d2dNo: string;
  noHeader?: boolean;
}

export const StatusFlowsContext = React.createContext<statusFlowsContextValues>({} as any);

interface BookingStatusFlowsProps {
  d2dNo: string;
  noHeader?: boolean;
  select?: {
    [K in keyof StatusFlows]?: true;
  };
}

export const BookingStatusFlows: React.FC<BookingStatusFlowsProps> = (props) => {
  const { d2dNo, select, noHeader } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };

  const isAdmin = useIsAdmin();

  const statusFlows = isAdmin
    ? dashboardBookingInquiryApis.useGet('/dashboard/booking-inquiry/:d2d_no/status-flows', {
        params: {
          d2d_no: d2dNo,
        },
      })
    : bookingInquiryApis.useGet('/booking-inquiry/:d2d_no/status-flows', {
        params: {
          d2d_no: d2dNo,
        },
      });
  const statusFlowsData = statusFlows.data;

  const shouldDisplaySection = (key: keyof StatusFlows) => {
    return select === undefined || select[key] || false;
  };

  const preAlertLink = `${url('/admin/booking/ExportPreAlert')}?${stringifySearch({
    d2dNo,
  })}`;

  const customsInstructionLink = `${url('/admin/booking/CustomsInstruction')}?${stringifySearch({
    d2dNo,
  })}`;

  const deliveryInstructionLink = `${url('/admin/booking/DeliveryInstruction')}?${stringifySearch({
    d2dNo,
  })}`;

  const customsRequestLink = `${url('/admin/booking/CustomsRequestDetails')}?${stringifySearch({
    d2d_no: d2dNo,
  })}`;

  const deliveryRequestLink = (ref?: string | null) =>
    `${url('/admin/booking/DeliveryRequestDetails')}?${stringifySearch({
      id: ref,
      d2d_no: d2dNo,
    })}`;

  const invoiceLink = (ref?: string | null) =>
    `${url('/admin/booking/InvoiceDetail')}?${stringifySearch({
      id: ref,
      d2dNo,
    })}`;

  const paymentSlipLink = (ref?: string | null) =>
    `${url('/admin/booking/PaymentSlipDetails')}?${stringifySearch({
      id: ref,
      d2d_no: d2dNo,
    })}`;

  const preAlertStatus = useMemo(
    () =>
      (statusFlowsData?.pre_alert_status_flow.status_flows ?? []).map((item) =>
        ['作成済', '修正中', 'LOCKED'].includes(item.name) ? { ...item, link: preAlertLink } : item,
      ),

    [statusFlowsData?.pre_alert_status_flow],
  );

  const customsStatus = useMemo(
    () =>
      (statusFlowsData?.customs_status_flow.status_flows ?? []).map((item) => {
        if (includes(InstructionStatusName.options, item.name)) {
          return {
            ...item,
            name: getInstructionStatusLabel(item.name, true),
            link: customsInstructionLink,
          };
        }
        if (
          includes(['通関依頼書作成済', '通関依頼書送信済', '通関リンク作成済'] as const, item.name)
        ) {
          return { ...item, link: customsRequestLink };
        }
        return item;
      }),
    [statusFlowsData?.customs_status_flow],
  );

  const deliveryStatus = useMemo(
    () =>
      (statusFlowsData?.delivery_status_flow.status_flows ?? []).map((item) => {
        if (includes(InstructionStatusName.options, item.name)) {
          return {
            ...item,
            name: getInstructionStatusLabel(item.name, true),
            link: deliveryInstructionLink,
          };
        }
        if (item.name === '配車依頼書作成済') {
          return { ...item, link: deliveryRequestLink(item.reference_id) };
        }
        return item;
      }),
    [statusFlowsData?.delivery_status_flow],
  );

  const doStatus = useMemo(
    () => statusFlowsData?.do_status_flow.status_flows ?? [],
    [statusFlowsData?.do_status_flow],
  );

  const moveOutStatus = useMemo(
    () => statusFlowsData?.move_out_flow.status_flows ?? [],
    [statusFlowsData?.move_out_flow],
  );

  const invoiceStatus = useMemo(
    () =>
      (statusFlowsData?.invoice_status_flow.status_flows ?? []).map((item) => {
        if (['発行済', '作成中'].includes(item.name)) {
          return { ...item, link: invoiceLink(item.reference_id) };
        }
        return item;
      }),
    [statusFlowsData?.invoice_status_flow],
  );

  const paymentSlipStatus = useMemo(
    () =>
      statusFlowsData && statusFlowsData?.payment_slip_status_flow.status_flows.length > 0
        ? statusFlowsData.payment_slip_status_flow.status_flows.map((item) => {
            if (['作成済', '下書き', '修正中'].includes(item.name)) {
              return { ...item, link: paymentSlipLink(item.reference_id) };
            }
            return item;
          })
        : [{ name: '未作成' } as const],
    [statusFlowsData?.payment_slip_status_flow],
  );

  const getNextStatusByLast = <S extends StatusDataWithLink>(
    status: S[],
    getNext: (s: S['name']) => S['name'] | '',
    getLabel?: (s: S['name']) => string,
  ) => {
    const lastStatus = status[status.length - 1];
    if (!lastStatus) {
      return undefined;
    }
    const nextStatus = getNext(lastStatus.name);
    if (nextStatus) {
      return {
        name: getLabel?.(nextStatus) ?? nextStatus,
      };
    } else {
      return undefined;
    }
  };

  return (
    <StatusFlowsContext.Provider
      value={{ d2dNo, invalidateStatusFlows: statusFlows.invalidate, noHeader }}
    >
      <QueryContainer queries={[statusFlows]}>
        <div className={clsx(classes.flexInline, classes.justifyStart)}>
          {shouldDisplaySection('pre_alert_status_flow') && (
            <StatusFlow
              title={'PRE ALERT'}
              statuses={preAlertStatus}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.pre_alert_status_flow.status_flows ?? [],
                StatusFlowsHelper.getNextPreAlertStatus,
              )}
              warning={statusFlowsData?.pre_alert_status_flow.warning}
            />
          )}
          {shouldDisplaySection('customs_status_flow') && (
            <StatusFlow
              title={strings.customsStatus}
              statuses={customsStatus}
              warning={statusFlowsData?.customs_status_flow.warning}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.customs_status_flow.status_flows ?? [],
                StatusFlowsHelper.getNextCustomsStatus,
                (status) => StatusFlowsHelper.getCustomsStatusLabel(status, true),
              )}
            />
          )}
          {shouldDisplaySection('delivery_status_flow') && (
            <StatusFlow
              title={strings.deliveryStatus}
              statuses={deliveryStatus}
              warning={statusFlowsData?.delivery_status_flow.warning}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.delivery_status_flow.status_flows ?? [],
                StatusFlowsHelper.getNextDeliveryStatus,
                (status) => StatusFlowsHelper.getDeliveryStatusLabel(status, true),
              )}
            />
          )}
          {shouldDisplaySection('do_status_flow') && (
            <StatusFlow
              title={strings.doStatus}
              statuses={doStatus}
              warning={statusFlowsData?.do_status_flow.warning}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.do_status_flow.status_flows ?? [],
                StatusFlowsHelper.getNextDOStatus,
              )}
            />
          )}
          {shouldDisplaySection('move_out_flow') && (
            <StatusFlow
              title={strings.moveOutStatus}
              statuses={moveOutStatus}
              warning={statusFlowsData?.move_out_flow.warning}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.move_out_flow.status_flows ?? [],
                (s) =>
                  StatusFlowsHelper.getNextMoveOutStatus(
                    s,
                    statusFlowsData?.delivery_destination_type!,
                  ),
              )}
            />
          )}
          {shouldDisplaySection('invoice_status_flow') && (
            <StatusFlow
              title={strings.invoiceStatus}
              statuses={invoiceStatus}
              warning={statusFlowsData?.invoice_status_flow.warning}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.invoice_status_flow.status_flows ?? [],
                StatusFlowsHelper.getNextInvoiceStatus,
              )}
            />
          )}
          {shouldDisplaySection('payment_slip_status_flow') && (
            <StatusFlow
              title={strings.paymentSlipStatus}
              statuses={paymentSlipStatus}
              warning={statusFlowsData?.payment_slip_status_flow.warning}
              nextStatus={getNextStatusByLast(
                statusFlowsData?.payment_slip_status_flow.status_flows ?? [],
                StatusFlowsHelper.getNextPaymentSlipStatus,
              )}
            />
          )}
        </div>
      </QueryContainer>
    </StatusFlowsContext.Provider>
  );
};

interface StatusDataWithLink extends StatusData {
  link?: string | null;
}

interface StatusFlowProps {
  title: string;
  statuses: StatusDataWithLink[];
  nextStatus?: StatusDataWithLink;
  warning?: boolean;
}

const StatusFlow: React.FC<StatusFlowProps> = (props) => {
  const { title, statuses, nextStatus, warning } = props;
  const [expanded, setExpanded] = useState(false);
  const classes = { ...useStyles(), ...useCommonStyles() };

  const { noHeader } = useContext(StatusFlowsContext);

  const lastStatusItem = statuses[statuses.length - 1];

  const flowStatus = (() => {
    if (includes(finishStatuses, lastStatusItem?.name)) {
      return 'done';
    } else if (warning) {
      return 'warning';
    } else if (statuses.length <= 1) {
      return 'initial';
    } else {
      return 'progress';
    }
  })();

  const lastColor =
    flowStatus === 'done' ? undefined : flowStatus === 'warning' ? 'error' : 'primary';

  return (
    <div className={classes.flex1} style={{ overflow: 'hidden' }}>
      {!noHeader && (
        <div className={clsx(classes.titleContainer, warning && classes.textErrorImportant)}>
          {flowStatus === 'done' && <img src={TickIcon} alt="tick" />}
          {flowStatus === 'progress' && <img src={InProgressIcon} alt="progress" />}
          {flowStatus === 'initial' && <img src={EmptyIcon} alt="empty" />}
          {flowStatus === 'warning' && <img src={CautionIcon} alt="caution" />}
          &nbsp;
          {title}
        </div>
      )}

      <div className={classes.itemsContainer}>
        {expanded &&
          statuses
            .slice(0, statuses.length - 1)
            .filter((item) => item.name !== '搬出オーダー送信待ち') // This status is transient
            .map((item, index) => <StatusItem statusItem={item} key={index} />)}

        {lastStatusItem && (
          <StatusItem statusItem={lastStatusItem} isLast status={flowStatus} color={lastColor} />
        )}
        {nextStatus && <StatusItem statusItem={nextStatus} color="next" />}

        {statuses.length > 1 ? (
          <div className={classes.button} onClick={() => setExpanded((s) => !s)}>
            {expanded ? strings.close : strings.seeMore}
          </div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

interface StatusItemProps {
  statusItem: StatusDataWithLink;
  isLast?: boolean;
  status?: 'done' | 'progress' | 'initial' | 'warning';
  color?: 'default' | 'primary' | 'error' | 'next';
}

const StatusItem: React.FC<StatusItemProps> = (props) => {
  const { statusItem, isLast = false, color, status } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  const { invalidateStatusFlows } = useContext(StatusFlowsContext);
  const [moveOutDialogOpen, setMoveOutDialogOpen] = useState(false);
  const [doDialogOpen, setDoDialogOpen] = useState(false);
  const [paymentSlipDialogOpen, setPaymentSlipDialogOpen] = useState(false);

  // Maybe the StatusItem is not under an admin BookingDetailsContext...
  const bookingDetailsContext = useContext(BookingDetailsContext) as
    | BookingDetailsContextValues
    | undefined;
  const [bookingInquiry, preAlert, user] = [
    bookingDetailsContext?.bookingInquiry,
    bookingDetailsContext?.preAlert,
    bookingDetailsContext?.user,
  ];

  const patchDeliveryRequest = dashboardDeliveryRequestApis.usePatch(
    '/dashboard/delivery-request/:id',
    { params: { id: Number(statusItem.reference_id!) } },
    { onSuccess: invalidateStatusFlows },
  );

  const putCustomsRequest = dashboardCustomsRequestApis.usePut(
    '/dashboard/customs-request/:d2d_no',
    {
      queries: { action: 'COMPLETE' },
      params: { d2d_no: bookingInquiry?.d2d_no! },
    },
    {
      onSuccess: invalidateStatusFlows,
    },
  );

  const canCompleteDeliveryRequest =
    isLast &&
    (
      ['メール送信済既読', '横持ち送信済', '配車リンク作成済'] satisfies DeliveryRequestStatusName[]
    ).some((s) => s === statusItem.name);

  const canUploadDO = isLast && statusItem.name === '支払伝票入金済';
  const canEditDO = isLast && statusItem.name === 'D/O手配済';

  const canUploadMoveOut = isLast && statusItem.name === '搬入先確認済';
  const canEditMoveOut = isLast && statusItem.name === '搬出手配済' && !!statusItem.attached_file;

  const canIssuePaymentSlip = isLast && statusItem.name === '支払伝票作成待ち';

  const canCompleteSubmitCustomsRequest =
    isLast &&
    bookingInquiry &&
    statusItem.name === ('通関リンク作成済' satisfies CustomsRequestStatusName);

  const hint =
    statusItem.name in strings.statusHints
      ? strings.statusHints[statusItem.name as keyof typeof strings.statusHints]
      : undefined;

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [patchDeliveryRequest],
  });

  const canEdit =
    bookingInquiry && user && preAlert
      ? new AdminInquiryDetailHelper(bookingInquiry).canEdit(user, preAlert)
      : false;

  return (
    <div className={classes.statusItem} aria-label={isLast ? strings.ariaLabelIsLast : undefined}>
      {bookingDetailsContext && (
        <>
          <PaymentSlipFormDialog
            open={paymentSlipDialogOpen}
            key={useDialogKey(paymentSlipDialogOpen)}
            onClose={() => setPaymentSlipDialogOpen(false)}
            bookingInquiry={bookingInquiry!}
            preAlert={preAlert!}
            type="create"
            isArrivalNoticeTag
          />
          {(canUploadMoveOut || canEditMoveOut) && (
            <MoveOutUpdateDialog
              open={moveOutDialogOpen}
              edit={canEditMoveOut}
              onClose={() => setMoveOutDialogOpen(false)}
              moveOutFile={statusItem.attached_file}
            />
          )}
          {(canUploadDO || canEditDO) && (
            <DOUpdateDialog
              open={doDialogOpen}
              edit={canEditDO}
              onClose={() => setDoDialogOpen(false)}
              DOFile={statusItem.attached_file}
              DONo={statusItem.message}
            />
          )}
        </>
      )}

      <div className={clsx(classes.dot, color)} />
      {renderAlertsAndSpinnerModal()}
      <BookingStatusItem
        {...statusItem}
        link={bookingDetailsContext && statusItem.link}
        color={color}
      />
      {statusItem.name === '顧客指示未' && isLast && status === 'warning' && (
        <div
          className={clsx(
            classes.statusName,
            isLast && status === 'warning' && classes.textErrorImportant,
          )}
        >
          ({strings.overdue})
        </div>
      )}

      {hint && color !== 'next' && (
        <div className={clsx(classes.smallerText, classes.textAccentFocus)}>{hint}</div>
      )}

      {statusItem.time && (
        <div
          className={clsx(
            classes.statusDate,
            isLast && status === 'warning' && classes.textErrorImportant,
          )}
        >
          {formatDateTime(statusItem.time)}
        </div>
      )}

      {bookingDetailsContext && (
        <>
          {(canEditDO || canEditMoveOut) && (
            <div
              className={clsx(classes.smallerText, classes.link, classes.underline)}
              onClick={() => {
                canEditDO ? setDoDialogOpen(true) : setMoveOutDialogOpen(true);
              }}
            >
              replace
            </div>
          )}

          {canCompleteDeliveryRequest && (
            <Button
              text={strings.deliveryRequestCompleted}
              color="primary"
              size="small"
              shape="round"
              onClick={async () =>
                await patchDeliveryRequest.mutateAsync({ status_name: '配車完了' })
              }
              disabled={!canEdit}
            />
          )}
          {canUploadDO && (
            <Button
              text={strings.completed}
              color="primary"
              size="small"
              shape="round"
              onClick={() => setDoDialogOpen(true)}
              disabled={!canEdit}
            />
          )}
          {canUploadMoveOut && (
            <Button
              text={strings.completed}
              color="primary"
              size="small"
              shape="round"
              onClick={() => setMoveOutDialogOpen(true)}
              disabled={!canEdit}
            />
          )}
          {canIssuePaymentSlip && (
            <Button
              text={strings.create}
              color="primary"
              size="small"
              shape="round"
              onClick={() => setPaymentSlipDialogOpen(true)}
              disabled={!canEdit}
            />
          )}
          {canCompleteSubmitCustomsRequest && (
            <Button
              text={strings.completeSubmitCustomsRequest}
              color="primary"
              size="small"
              shape="round"
              onClick={async () => await putCustomsRequest.mutateAsync({})}
              disabled={!canEdit}
            />
          )}
        </>
      )}
    </div>
  );
};
